/**
 * VariablePicker Component
 * Dropdown component for selecting and inserting variables into text content
 * Supports {{variableName}} syntax for template variables
 */

import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Braces,
  Search,
  User,
  Building2,
  Calendar,
  Mail,
  Phone,
  Hash,
  FileText,
  Globe,
  Clock,
  DollarSign,
  Percent,
  Link,
  AtSign,
} from 'lucide-react'

// ============================================
// Types
// ============================================

export interface Variable {
  name: string
  displayName: string
  description?: string
  category: VariableCategory
  example?: string
}

export type VariableCategory =
  | 'User'
  | 'Company'
  | 'Date'
  | 'Contact'
  | 'Financial'
  | 'Document'
  | 'Custom'

export interface VariablePickerProps {
  onSelect: (variable: Variable) => void
  customVariables?: Variable[]
  disabled?: boolean
  className?: string
}

// ============================================
// Default Variables
// ============================================

const CATEGORY_ICONS: Record<VariableCategory, React.ElementType> = {
  User: User,
  Company: Building2,
  Date: Calendar,
  Contact: Mail,
  Financial: DollarSign,
  Document: FileText,
  Custom: Braces,
}

const CATEGORY_COLORS: Record<VariableCategory, string> = {
  User: 'text-blue-500',
  Company: 'text-purple-500',
  Date: 'text-green-500',
  Contact: 'text-rose-500',
  Financial: 'text-amber-500',
  Document: 'text-cyan-500',
  Custom: 'text-neutral-500',
}

const VARIABLE_ICONS: Record<string, React.ElementType> = {
  userName: User,
  userEmail: Mail,
  userPhone: Phone,
  userTitle: AtSign,
  companyName: Building2,
  companyAddress: Globe,
  companyPhone: Phone,
  companyEmail: Mail,
  companyWebsite: Link,
  date: Calendar,
  time: Clock,
  year: Hash,
  month: Hash,
  day: Hash,
  documentTitle: FileText,
  documentAuthor: User,
  amount: DollarSign,
  currency: DollarSign,
  percentage: Percent,
}

export const DEFAULT_VARIABLES: Variable[] = [
  // User variables
  {
    name: 'userName',
    displayName: 'User Name',
    description: 'Full name of the current user',
    category: 'User',
    example: 'John Smith',
  },
  {
    name: 'userFirstName',
    displayName: 'User First Name',
    description: 'First name of the current user',
    category: 'User',
    example: 'John',
  },
  {
    name: 'userLastName',
    displayName: 'User Last Name',
    description: 'Last name of the current user',
    category: 'User',
    example: 'Smith',
  },
  {
    name: 'userEmail',
    displayName: 'User Email',
    description: 'Email address of the current user',
    category: 'User',
    example: 'john.smith@example.com',
  },
  {
    name: 'userTitle',
    displayName: 'User Title',
    description: 'Job title of the current user',
    category: 'User',
    example: 'Senior Manager',
  },
  {
    name: 'userDepartment',
    displayName: 'User Department',
    description: 'Department of the current user',
    category: 'User',
    example: 'Marketing',
  },

  // Company variables
  {
    name: 'companyName',
    displayName: 'Company Name',
    description: 'Name of the company/organization',
    category: 'Company',
    example: 'ACME Corporation',
  },
  {
    name: 'companyAddress',
    displayName: 'Company Address',
    description: 'Full address of the company',
    category: 'Company',
    example: '1 Example Street',
  },
  {
    name: 'companyCity',
    displayName: 'Company City',
    description: 'City where the company is located',
    category: 'Company',
    example: 'New York',
  },
  {
    name: 'companyCountry',
    displayName: 'Company Country',
    description: 'Country where the company is located',
    category: 'Company',
    example: 'United States',
  },
  {
    name: 'companyPhone',
    displayName: 'Company Phone',
    description: 'Main phone number of the company',
    category: 'Company',
    example: '+1 (555) 123-4567',
  },
  {
    name: 'companyEmail',
    displayName: 'Company Email',
    description: 'Main email address of the company',
    category: 'Company',
    example: 'info@acmecorp.com',
  },
  {
    name: 'companyWebsite',
    displayName: 'Company Website',
    description: 'Website URL of the company',
    category: 'Company',
    example: 'www.acmecorp.com',
  },

  // Date variables
  {
    name: 'date',
    displayName: 'Current Date',
    description: 'Today\'s date in default format',
    category: 'Date',
    example: 'January 15, 2024',
  },
  {
    name: 'dateShort',
    displayName: 'Date (Short)',
    description: 'Today\'s date in short format',
    category: 'Date',
    example: '01/15/2024',
  },
  {
    name: 'time',
    displayName: 'Current Time',
    description: 'Current time',
    category: 'Date',
    example: '2:30 PM',
  },
  {
    name: 'year',
    displayName: 'Current Year',
    description: 'Current year',
    category: 'Date',
    example: '2024',
  },
  {
    name: 'month',
    displayName: 'Current Month',
    description: 'Current month name',
    category: 'Date',
    example: 'January',
  },
  {
    name: 'day',
    displayName: 'Day of Month',
    description: 'Current day of the month',
    category: 'Date',
    example: '15',
  },
  {
    name: 'weekday',
    displayName: 'Day of Week',
    description: 'Current day of the week',
    category: 'Date',
    example: 'Monday',
  },

  // Contact variables
  {
    name: 'recipientName',
    displayName: 'Recipient Name',
    description: 'Name of the document recipient',
    category: 'Contact',
    example: 'Jane Doe',
  },
  {
    name: 'recipientEmail',
    displayName: 'Recipient Email',
    description: 'Email of the document recipient',
    category: 'Contact',
    example: 'jane.doe@client.com',
  },
  {
    name: 'recipientTitle',
    displayName: 'Recipient Title',
    description: 'Job title of the recipient',
    category: 'Contact',
    example: 'Director of Operations',
  },
  {
    name: 'recipientCompany',
    displayName: 'Recipient Company',
    description: 'Company name of the recipient',
    category: 'Contact',
    example: 'Client Corp',
  },

  // Financial variables
  {
    name: 'amount',
    displayName: 'Amount',
    description: 'Monetary amount',
    category: 'Financial',
    example: '$1,234.56',
  },
  {
    name: 'currency',
    displayName: 'Currency',
    description: 'Currency code or symbol',
    category: 'Financial',
    example: 'USD',
  },
  {
    name: 'taxRate',
    displayName: 'Tax Rate',
    description: 'Tax rate percentage',
    category: 'Financial',
    example: '8.5%',
  },
  {
    name: 'subtotal',
    displayName: 'Subtotal',
    description: 'Subtotal before tax',
    category: 'Financial',
    example: '$1,000.00',
  },
  {
    name: 'total',
    displayName: 'Total',
    description: 'Total amount including tax',
    category: 'Financial',
    example: '$1,085.00',
  },

  // Document variables
  {
    name: 'documentTitle',
    displayName: 'Document Title',
    description: 'Title of the document',
    category: 'Document',
    example: 'Q1 Sales Report',
  },
  {
    name: 'documentAuthor',
    displayName: 'Document Author',
    description: 'Author of the document',
    category: 'Document',
    example: 'John Smith',
  },
  {
    name: 'documentDate',
    displayName: 'Document Date',
    description: 'Date the document was created',
    category: 'Document',
    example: 'January 15, 2024',
  },
  {
    name: 'documentVersion',
    displayName: 'Document Version',
    description: 'Version number of the document',
    category: 'Document',
    example: 'v1.0',
  },
  {
    name: 'pageNumber',
    displayName: 'Page Number',
    description: 'Current page number',
    category: 'Document',
    example: '1',
  },
  {
    name: 'totalPages',
    displayName: 'Total Pages',
    description: 'Total number of pages',
    category: 'Document',
    example: '10',
  },
]

// ============================================
// Variable Item Component
// ============================================

interface VariableItemProps {
  variable: Variable
  onClick: () => void
}

function VariableItem({ variable, onClick }: VariableItemProps) {
  const VariableIcon = VARIABLE_ICONS[variable.name] || Braces

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-3 p-2.5 rounded-lg text-left',
        'hover:bg-neutral-100 dark:hover:bg-neutral-800',
        'transition-colors duration-150',
        'focus:outline-none focus:ring-2 focus:ring-blue-500/20'
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 p-1.5 rounded-md',
          'bg-neutral-100 dark:bg-neutral-800',
          CATEGORY_COLORS[variable.category]
        )}
      >
        <VariableIcon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-neutral-900 dark:text-white">
            {variable.displayName}
          </span>
          <code className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 font-mono">
            {`{{${variable.name}}}`}
          </code>
        </div>
        {variable.description && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-1">
            {variable.description}
          </p>
        )}
        {variable.example && (
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5 italic">
            e.g., {variable.example}
          </p>
        )}
      </div>
    </button>
  )
}

// ============================================
// Category Header Component
// ============================================

interface CategoryHeaderProps {
  category: VariableCategory
  count: number
}

function CategoryHeader({ category, count }: CategoryHeaderProps) {
  const Icon = CATEGORY_ICONS[category]

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 bg-neutral-50 dark:bg-neutral-900 rounded-md mb-1">
      <Icon className={cn('h-3.5 w-3.5', CATEGORY_COLORS[category])} />
      <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
        {category}
      </span>
      <span className="text-xs text-neutral-400 dark:text-neutral-500 ml-auto">
        {count}
      </span>
    </div>
  )
}

// ============================================
// VariablePicker Component
// ============================================

export function VariablePicker({
  onSelect,
  customVariables = [],
  disabled = false,
  className,
}: VariablePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  // Combine default and custom variables
  const allVariables = useMemo(() => {
    return [...DEFAULT_VARIABLES, ...customVariables]
  }, [customVariables])

  // Filter variables based on search
  const filteredVariables = useMemo(() => {
    if (!search.trim()) return allVariables

    const searchLower = search.toLowerCase()
    return allVariables.filter(
      (v) =>
        v.name.toLowerCase().includes(searchLower) ||
        v.displayName.toLowerCase().includes(searchLower) ||
        v.description?.toLowerCase().includes(searchLower) ||
        v.category.toLowerCase().includes(searchLower)
    )
  }, [allVariables, search])

  // Group variables by category
  const groupedVariables = useMemo(() => {
    const groups: Record<VariableCategory, Variable[]> = {
      User: [],
      Company: [],
      Date: [],
      Contact: [],
      Financial: [],
      Document: [],
      Custom: [],
    }

    filteredVariables.forEach((variable) => {
      groups[variable.category].push(variable)
    })

    return groups
  }, [filteredVariables])

  // Handle variable selection
  const handleSelect = (variable: Variable) => {
    onSelect(variable)
    setIsOpen(false)
    setSearch('')
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn(
            'gap-1.5 h-8 px-2.5 rounded-lg',
            'border-neutral-200 dark:border-neutral-700',
            'hover:bg-neutral-100 dark:hover:bg-neutral-800',
            className
          )}
        >
          <Braces className="h-4 w-4 text-blue-500" />
          <span className="text-sm">Variables</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 rounded-xl shadow-lg"
        align="start"
        sideOffset={5}
      >
        {/* Search Header */}
        <div className="p-3 border-b border-neutral-200 dark:border-neutral-700">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              type="text"
              placeholder="Search variables..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 rounded-lg text-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Variables List */}
        <ScrollArea className="h-[320px]">
          <div className="p-2">
            {filteredVariables.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                <Braces className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No variables found</p>
                <p className="text-xs mt-1">Try a different search term</p>
              </div>
            ) : (
              Object.entries(groupedVariables).map(([category, variables]) => {
                if (variables.length === 0) return null

                return (
                  <div key={category} className="mb-3 last:mb-0">
                    <CategoryHeader
                      category={category as VariableCategory}
                      count={variables.length}
                    />
                    <div className="space-y-0.5">
                      {variables.map((variable) => (
                        <VariableItem
                          key={variable.name}
                          variable={variable}
                          onClick={() => handleSelect(variable)}
                        />
                      ))}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </ScrollArea>

        {/* Footer Hint */}
        <div className="p-2 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-b-xl">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
            Variables use <code className="font-mono">{`{{name}}`}</code> syntax
          </p>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default VariablePicker
